import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import hLogoImg from './HLOGO.png';
import hIconImg from './HPLOGO.png';
import hpAiImg from './HPAI.png';
import ApplyForm from './components/ApplyForms';
import AuthPortal from './components/AuthPortal';
import AdminDashboard from './components/AdminDashboard';
import StaffDashboard from './components/StaffDashboard';
import ApplicantSupportWidget from './components/ApplicantSupportWidget';
import ApplicationWaiting from './components/ApplicationWaiting';
import AssessmentPage from './components/AssessmentPage';
import { resolveApplicantAccess } from './utils/applicantAccess';
import { isHandheldDevice } from './utils/deviceDetect';
import {
  isGroupedConversationId,
  markConversationsDeleted,
  mergeApiNotifications,
  normalizeConversationEmail,
  getConversationId,
  parseApiDateTime,
  normalizeMessageCreatedAt as normalizeCreatedAtValue,
  resolveEmailsFromDeleteIds,
  unmarkConversationDeleted,
  getHiddenMessageIds,
  getHiddenMessagesUserKey,
  hideMessageForUser,
} from './utils/messageConversations';
import {
  parseAssignmentsResponse
} from './utils/assignmentHelpers';
import { isHeadAdminUser, isDepartmentAdminUser } from './utils/adminAccess';
import { buildReviewerPayload } from './utils/reviewPipeline';
import { getStoredLang, storeLang, getT, getPositionLabel } from './i18n';
import { filterSubmittedJobApplications } from './utils/applicationFilters';
import {
  fetchJobDescriptionsMap,
  resolvePositionDescription,
  JOB_DESCRIPTIONS_UPDATED_EVENT,
} from './utils/jobDescriptions';
import { API_URL } from './config/api';
import {
  clearAuthSession,
  getInitialAuthState,
  loadAuthView,
  saveAuthSession,
  saveAuthView,
  syncAuthSessionSnapshot,
  updateAuthSession,
} from './utils/authSession';

const ContactLocationIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const ContactEmailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ContactPhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 4h3l1.5 4-2 1.5a11.5 11.5 0 005.5 5.5L18 13l4 1.5v3A2 2 0 0119.8 19.6 16 16 0 014.4 4.2 2 2 0 016.5 4z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContactWebsiteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ==============================================
// DICTIONARY LOOKUP PARA SA ENGLISH AT CHINESE
// ==============================================
const translations = {
  en: {
    home: "Home",
    category: "Category",
    aboutUs: "About Us",
    contact: "Contact",
    assessment: "Assessment",
    login: "Login",
    signUp: "Sign Up",
    heroDesc: "Empowering everyone to work as an expert. Join Helport and build a sustainable career in the digital era through expert-led opportunities and innovative systems.",
    heroTitle: "Build your career with",
    heroTitleAccent: "AI-powered expertise.",
    heroChip1: "AI-powered tools",
    heroChip2: "Expert-led teams",
    nowHiring: "Now Hiring",
    applyNow: "Apply Now",
    learnMore: "Learn more",
    aboutTitle: "ABOUT HELPORT",
    aboutStrong: "Helport Philippines Branch Office (HPBO)",
    aboutText1: " is dedicated to empowering individuals by providing expert-led opportunities in the digital era. We bridge the gap between talent and technology, utilizing cutting-edge AI synergy to create a sustainable and innovative work environment.",
    aboutText2: "We adhere strictly to the principles of non-discrimination and Equal Opportunity Employment. Regardless of age, gender, educational background, race, skin color, religious affiliation, or ethnicity, everyone has a place here to grow and succeed.",
    missionTitle: "Our Mission",
    missionDesc: "Helport AI’s mission is to empower everyone to work as an expert through AI-driven tools and intelligent software - elevating human performance in customer interactions rather than replacing people.",
    visionTitle: "Our Vision",
    visionDesc: "To transform customer service and the contact center industry by integrating advanced AI with human agents - empowering people to deliver expert-level service and elevating the standard of customer communication worldwide.",
    getInTouch: "GET IN TOUCH",
    contactInfo: "Contact Information",
    contactSub: "Have questions about your application or our platform? Reach out to us!",
    contactAccountRequired: "Please create an account and log in first before sending a message. Sign up or log in to view admin replies.",
    address: "23rd Floor, IBM Plaza, Eastwood City Cyberpark, 188 E. Rodriguez Jr. Ave., Bagumbayan, Quezon City, Philippines",
    name: "Name",
    namePlaceholder: "Your Name",
    email: "Email Address",
    emailPlaceholder: "Your Email",
    message: "Message",
    messagePlaceholder: "How can we help you?",
    sendMessage: "Send Message",
    supportedBy: "Supported by Helport",
    categoryIntro: "Explore the different roles and services available on Helport. Choose the category that fits your skills and interests.",
    dashboard: "Dashboard",
    applicantMessages: "Messages",
    helportPhilippines: "Helport Philippines"
  },
  zh: {
    home: "主页",
    category: "类别",
    aboutUs: "关于我们",
    contact: "联系我们",
    assessment: "评估",
    login: "登录",
    signUp: "注册",
    heroDesc: "赋能每一个人成为专家。加入 Helport，通过专家引导的机会和创新系统，在数字时代建立可持续发展的职业生涯。",
    heroTitle: "开启您的职业生涯，",
    heroTitleAccent: "由 AI 专业赋能。",
    heroChip1: "AI 驱动工具",
    heroChip2: "专家带队",
    nowHiring: "正在招聘",
    applyNow: "立即申请",
    learnMore: "了解更多",
    aboutTitle: "关于 HELPORT",
    aboutStrong: "Helport 菲律宾分公司 (HPBO)",
    aboutText1: " 致力于通过在数字时代提供专家引导的机会来赋能个人。我们架起人才与技术之间的桥梁，利用尖端的人工智能协同效应，创造一个可持续且创新的工作环境。",
    aboutText2: "我们严格遵守非歧视和平等就业机会原则。无论年龄、性别、教育背景、种族、肤色、宗教稳步或民族，每个人都有在这里成长和获得成功的空间。",
    missionTitle: "我们的使命",
    missionDesc: "Helport AI 的使命是通过人工智能驱动的工具 and 智能软件来赋能每个人以专家的身份进行工作 - 提升人类在客户互动中的表现，而不是取代人类。",
    visionTitle: "我们的愿景",
    visionDesc: "通过将先进的人工智能与人工坐席相结合，改变客户服务和呼叫中心行业 - 赋能人们提供专家级别的服务，并提升全球客户沟通的标准。",
    getInTouch: "取得联系",
    contactInfo: "联系信息",
    contactSub: "对您的申请或我们的平台有疑问吗？联系我们！",
    contactAccountRequired: "请先创建账户并登录后再发送消息。注册或登录后即可查看管理员的回复。",
    address: "菲律宾奎松市 Bagumbayan, E. Rodriguez Jr. Ave. 188号 Eastwood City Cyberpark, IBM Plaza 23楼",
    name: "姓名",
    namePlaceholder: "您的姓名",
    email: "电子邮件地址",
    emailPlaceholder: "您的电子邮件",
    message: "留言信息",
    messagePlaceholder: "我们能为您提供什么帮助？",
    sendMessage: "发送信息",
    supportedBy: "由 Helport 提供支持",
    categoryIntro: "探索 Helport 提供的各类职位与服务。选择符合您技能与兴趣的类别。",
    dashboard: "控制台",
    applicantMessages: "消息",
    helportPhilippines: "Helport 菲律宾"
  }
};

function App() {
  const initialAuth = getInitialAuthState();
  const [currentView, setCurrentView] = useState(initialAuth.currentView);
  const [lang, setLang] = useState(getStoredLang);
  const [applyInitialStep, setApplyInitialStep] = useState(1);
  const [applyMode, setApplyMode] = useState('new');
  const t = translations[lang] || translations.en;
  const ui = getT(lang);

  const handleLangChange = (nextLang) => {
    setLang(nextLang);
    storeLang(nextLang);
  };

  const positions = [
    'Accounting Staff / Accounting Officer',
    'Admin Manager',
    'Assistant Manager / Operations Manager',
    'Business Developer Manager',
    'Content Moderator',
    'Customer Service Representative',
    'Data Labeler',
    'Debt Collection Agent (Local)',
    'HR Specialist / HR Generalist',
    'IT Support',
    'Operations Supervisor',
    'Outbound Sales Agent',
    'Project Manager',
    'QA Specialist / Analist',
    'Recruiter',
    'Software Specialist',
    'Team Leader',
    'Trainer',
    'Executive Assistant / Executive Secretary',
    'Others',
  ];

  const [selectedPosition, setSelectedPosition] = useState(null);
  const [savedJobDescriptions, setSavedJobDescriptions] = useState({});

  // ==============================================
  // AUTHENTICATION STATES
  // ==============================================
  const [isLoggedIn, setIsLoggedIn] = useState(initialAuth.isLoggedIn);
  const [currentUser, setCurrentUser] = useState(initialAuth.currentUser);
  const [authBootstrapped, setAuthBootstrapped] = useState(() => !initialAuth.isLoggedIn);

  // --- GLOBAL RECRUITMENT STATES ---
  const [applications, setApplications] = useState([]);
  const [applicantApplication, setApplicantApplication] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [notesInputs, setNotesInputs] = useState({});
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('helport_messages') || '[]');
    } catch {
      return [];
    }
  });

    const persistMessages = (nextMessages) => {
      try {
        localStorage.setItem('helport_messages', JSON.stringify(nextMessages));
      } catch (error) {
        console.error('Error saving messages to localStorage:', error);
      }
      window.dispatchEvent(new Event('helport_messages_updated'));
      return nextMessages;
    };

    const normalizeMessageCreatedAt = (value) => normalizeCreatedAtValue(value);

    const resolveConversationTarget = (messageId, snapshot = []) => {
      if (isGroupedConversationId(messageId)) {
        const email = messageId.slice(5);
        const thread = snapshot.filter(
          (msg) => normalizeConversationEmail(msg.email) === normalizeConversationEmail(email)
        );
        const named = thread.find((msg) => (msg.name || '').trim());
        return {
          email,
          name: named?.name || thread[0]?.name || 'Applicant'
        };
      }

      const match = snapshot.find((msg) => String(msg.id) === String(messageId));
      return {
        email: match?.email || '',
        name: match?.name || 'Applicant'
      };
    };

    const handleAddReply = async (messageId, replyText) => {
      if (!replyText || !replyText.trim()) return false;

      const text = replyText.trim();
      const isApplicant = currentUser?.role === 'Applicant' && Boolean(currentUser?.email);

      let snapshot = messages;
      try {
        snapshot = JSON.parse(localStorage.getItem('helport_messages') || '[]');
        if (!Array.isArray(snapshot)) snapshot = messages;
      } catch {
        snapshot = messages;
      }

      let emailToUse = '';
      let nameToUse = 'Applicant';
      let senderLabel = '';
      let senderRole = 'applicant';

      if (isApplicant) {
        emailToUse = currentUser.email.trim();
        nameToUse = (currentUser.full_name || currentUser.name || 'Applicant').trim();
        senderLabel = `${nameToUse} - Applicant`;
        senderRole = 'applicant';
      } else {
        const target = resolveConversationTarget(messageId, snapshot);
        emailToUse = (target.email || '').trim();
        nameToUse = (target.name || 'Applicant').trim();
        if (!emailToUse) return false;

        const authorName = (currentUser && (currentUser.full_name || currentUser.name || currentUser.email)) || 'Admin';
        const authorRole = (currentUser && (currentUser.role || currentUser.position)) || 'Admin';
        senderLabel = `${authorName} at ${currentUser?.position || authorRole}`;
        senderRole = 'admin';
      }

      try {
        const response = await fetch(`${API_URL}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'send_chat_message',
            email: emailToUse,
            name: nameToUse,
            message: text,
            sender_role: senderRole,
            sender_name: senderLabel
          })
        });
        const result = await response.json();
        if (result.status !== 'success') {
          console.error('Failed to send chat message:', result.message);
          window.alert(result.message || 'Failed to send message. Please try again.');
          return false;
        }

        unmarkConversationDeleted(emailToUse);

        if (isApplicant) {
          await fetchNotificationsFromAPI(emailToUse);
        } else {
          const rows = await fetchContactMessagesFromAPI();
          if (rows) {
            setMessages((prev) => persistMessages(mergeContactThreadMessages(prev, rows)));
          }
        }

        return true;
      } catch (error) {
        console.error('Error sending chat message:', error);
        window.alert('Cannot connect to server. Make sure Apache/MySQL is running and api.php is updated.');
        return false;
      }
    };

  const deleteNotificationsForEmail = async (email) => {
    if (!email) return;
    try {
      await fetch(`${API_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete_notifications',
          applicant_email: email
        })
      });
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  const handleDeleteMessage = (messageIdOrIds) => {
    const ids = Array.isArray(messageIdOrIds) ? messageIdOrIds : [messageIdOrIds];
    if (!ids.length) return;

    const emailsToDelete = resolveEmailsFromDeleteIds(messages, ids);
    if (!emailsToDelete.length) {
      setMessages((prev) => prev.filter((m) => !ids.includes(m.id)));
      return;
    }

    markConversationsDeleted(emailsToDelete);
    emailsToDelete.forEach((email) => {
      deleteNotificationsForEmail(email);
    });

    setMessages((prev) =>
      prev.filter((m) => !emailsToDelete.includes(normalizeConversationEmail(m.email)))
    );
  };

  const [hiddenMessagesRevision, setHiddenMessagesRevision] = useState(0);

  const hiddenMessageIds = useMemo(() => {
    void hiddenMessagesRevision;
    return getHiddenMessageIds(getHiddenMessagesUserKey(currentUser));
  }, [currentUser, hiddenMessagesRevision]);

  const handleEditChatMessage = async (notificationId, newText) => {
    const trimmed = (newText || '').trim();
    if (!notificationId || !trimmed) return false;

    const isApplicant = currentUser?.role === 'Applicant';
    const requesterRole = isApplicant ? 'applicant' : 'admin';

    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'edit_chat_message',
          message_id: notificationId,
          message: trimmed,
          requester_role: requesterRole,
          requester_email: currentUser?.email || ''
        })
      });
      const result = await response.json();
      if (result.status !== 'success') {
        window.alert(result.message || 'Failed to edit message.');
        return false;
      }

      setMessages((prev) => {
        const next = prev.map((msg) => (
          String(msg.id) === String(notificationId) ? { ...msg, message: trimmed } : msg
        ));
        return persistMessages(next);
      });

      if (isApplicant) {
        await fetchNotificationsFromAPI(currentUser.email);
      } else {
        const rows = await fetchContactMessagesFromAPI();
        if (rows) {
          setMessages((prev) => persistMessages(mergeContactThreadMessages(prev, rows)));
        }
      }
      return true;
    } catch (error) {
      console.error('Error editing chat message:', error);
      window.alert('Cannot connect to server. Please try again.');
      return false;
    }
  };

  const handleDeleteChatMessage = async (notificationId, scope) => {
    if (!notificationId) return false;

    const userKey = getHiddenMessagesUserKey(currentUser);

    if (scope === 'for_me') {
      hideMessageForUser(userKey, notificationId);
      setHiddenMessagesRevision((value) => value + 1);
      return true;
    }

    const isApplicant = currentUser?.role === 'Applicant';
    const requesterRole = isApplicant ? 'applicant' : 'admin';

    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete_chat_message',
          message_id: notificationId,
          requester_role: requesterRole,
          requester_email: currentUser?.email || ''
        })
      });
      const result = await response.json();
      if (result.status !== 'success') {
        window.alert(result.message || 'Failed to delete message.');
        return false;
      }

      setMessages((prev) => {
        const next = prev.filter((msg) => String(msg.id) !== String(notificationId));
        return persistMessages(next);
      });

      if (isApplicant) {
        await fetchNotificationsFromAPI(currentUser.email);
      } else {
        const rows = await fetchContactMessagesFromAPI();
        if (rows) {
          setMessages((prev) => persistMessages(mergeContactThreadMessages(prev, rows)));
        }
      }
      return true;
    } catch (error) {
      console.error('Error deleting chat message:', error);
      window.alert('Cannot connect to server. Please try again.');
      return false;
    }
  };


  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactNotice, setContactNotice] = useState('');
  const [contactNoticeType, setContactNoticeType] = useState('success');

  const fetchContactMessagesFromAPI = async () => {
    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_contact_messages' })
      });
      const result = await response.json();
      if (result.status !== 'success' || !Array.isArray(result.data)) return null;
      return result.data.map((row) => {
        const meta = row.missing_documents && typeof row.missing_documents === 'object'
          ? row.missing_documents
          : {};
        return {
          id: row.id,
          name: row.applicant_name || 'Applicant',
          email: row.applicant_email || '',
          message: row.message || '',
          createdAt: normalizeMessageCreatedAt(row.created_at),
          replies: [],
          sender: meta.sender || '',
          notification_type: row.notification_type || 'contact_message'
        };
      });
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      return null;
    }
  };

  // Merge API chat rows while keeping any legacy local-only replies.
  const mergeContactThreadMessages = (previousMessages = [], apiRows = []) => {
    const previousById = new Map(previousMessages.map((msg) => [String(msg.id), msg]));
    const apiIdSet = new Set(apiRows.map((row) => String(row.id)));

    const mergedApiRows = apiRows.map((row) => {
      const previous = previousById.get(String(row.id));
      return {
        ...row,
        replies: previous?.replies || []
      };
    });

    // Drop stale local-* chat copies once the same thread exists on the server.
    const localOnly = previousMessages.filter((item) => {
      const id = String(item.id);
      if (apiIdSet.has(id)) return false;
      if (id.startsWith('local-')) {
        const email = normalizeConversationEmail(item.email);
        const hasServerThread = apiRows.some(
          (row) => normalizeConversationEmail(row.email) === email
        );
        if (hasServerThread && (item.notification_type === 'contact_message' || item.notification_type === 'admin_reply')) {
          return false;
        }
      }
      return true;
    });

    return [...mergedApiRows, ...localOnly].sort(
      (a, b) => (parseApiDateTime(b.createdAt)?.getTime() || 0) - (parseApiDateTime(a.createdAt)?.getTime() || 0)
    );
  };

  const fetchApplicantApplication = async (email) => {
    if (!email) return null;
    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_app_by_email',
          applicant_email: email
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        const app = result.data || null;
        setApplicantApplication(app);
        return app;
      }
    } catch (error) {
      console.error('Error fetching applicant application:', error);
    }
    setApplicantApplication(null);
    return null;
  };

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API_URL}?action=get_apps`);
      const text = await response.text();
      if (!text || text.trim() === "") { setApplications([]); return; }
      const data = JSON.parse(text);
      if (data && data.status === 'success') {
        setApplications(filterSubmittedJobApplications(Array.isArray(data.data) ? data.data : []));
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const fetchAssignments = async () => {
    const endpoints = [
      `${API_URL}?action=get_assignments`,
      {
        url: `${API_URL}`,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get_assignments' })
        }
      }
    ];

    for (const endpoint of endpoints) {
      try {
        const response = typeof endpoint === 'string'
          ? await fetch(endpoint)
          : await fetch(endpoint.url, endpoint.init);
        const rawText = await response.text();
        if (!rawText || !rawText.trim()) continue;

        let result = null;
        try {
          result = JSON.parse(rawText);
        } catch (parseError) {
          console.error('Assignment fetch parse error:', parseError, rawText);
          continue;
        }

        const normalized = parseAssignmentsResponse(result);
        if (normalized) {
          setAssignments(normalized);
          return normalized;
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    }

    return null;
  };

  const saveAssignment = async (applicationId, interviewerId, interviewerEmail = '') => {
    try {
      const response = await fetch(`${API_URL}?action=save_assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: Number(applicationId),
          interviewer_id: Number(interviewerId),
          interviewer_email: interviewerEmail || undefined
        })
      });
      const rawText = await response.text();
      let result = null;
      try {
        result = JSON.parse(rawText);
      } catch (parseError) {
        console.error('Assignment response parse error:', parseError, rawText);
        return {
          success: false,
          message: 'Invalid server response. Please ensure api.php is updated in XAMPP.'
        };
      }

      if (result.status === 'success') {
        await fetchAssignments();
        return {
          success: true,
          message: result.message || 'Assignment saved.',
          alreadyAssigned: /already assigned/i.test(result.message || '')
        };
      }

      console.error('Assignment save failed:', result.message);
      return {
        success: false,
        message: result.message || 'Assignment save failed.'
      };
    } catch (error) {
      console.error('Error saving assignment:', error);
      return {
        success: false,
        message: error.message || 'Cannot connect to server.'
      };
    }
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    const currentNote = notesInputs[appId] || '';
    const reviewer = buildReviewerPayload(currentUser);
    try {
      const response = await fetch(`${API_URL}?action=update_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: appId,
          newStatus: newStatus,
          remarks: currentNote,
          ...reviewer,
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setNotesInputs(prev => ({ ...prev, [appId]: '' }));
        await fetchApplications();
        if (String(newStatus).trim().toLowerCase() === 'hired') {
          await fetchAssignments();
          if (data.data?.email_sent) {
            const sentTo = data.data.resolved_email || 'applicant';
            alert(`✓ Applicant marked as Hired. Congratulations email sent to ${sentTo}.`);
          } else if (data.data) {
            alert(`Applicant marked as Hired, but email was not sent.${data.data.email_error ? `\n\nReason: ${data.data.email_error}` : ''}`);
          }
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleClaimReview = async (appId) => {
    if (!currentUser || !appId) return null;
    const reviewer = buildReviewerPayload(currentUser);
    if (!reviewer.reviewer_department || !reviewer.reviewer_name) return null;

    try {
      const response = await fetch(`${API_URL}?action=claim_review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appId, ...reviewer })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const history = data.data?.review_history;
        if (history) {
          setApplications((prev) =>
            (prev || []).map((app) =>
              String(app.id) === String(appId)
                ? { ...app, review_history: history }
                : app
            )
          );
        } else {
          await fetchApplications();
        }
        return data.data || null;
      }
    } catch (error) {
      console.error('Error claiming review:', error);
    }
    return null;
  };

  const handleAssignAccount = async (appId, accountName) => {
    try {
      const response = await fetch(`${API_URL}?action=assign_account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appId, assignedAccount: accountName })
      });
      const data = await response.json();
      if (data.status === 'success') {
        await fetchApplications();
      }
    } catch (error) {
      console.error('Error assigning account:', error);
    }
  };

  const handleRetractApplication = async (appId) => {
    try {
      const response = await fetch(`${API_URL}?action=retract_application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appId })
      });
      const data = await response.json();
      if (data.status === 'success') {
        await fetchApplications();
        await fetchAssignments();
      }
    } catch (error) {
      console.error('Error retracting application:', error);
    }
  };

  const handleNoteChange = (appId, text) => {
    setNotesInputs(prev => ({ ...prev, [appId]: text }));
  };

  const saveInterviewDate = async (applicationId, interviewDate, scheduledBy = '') => {
    try {
      const response = await fetch(`${API_URL}?action=set_interview_date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: Number(applicationId),
          interview_date: interviewDate,
          scheduled_by: scheduledBy || undefined
        })
      });
      const result = await response.json();
      if (result.status === 'success') {
        const storedDate = result.data?.interview_date || interviewDate;
        setApplications((prev) =>
          (prev || []).map((app) =>
            String(app.id) === String(applicationId)
              ? { ...app, interview_date: storedDate }
              : app
          )
        );
        fetchApplications().catch((error) => {
          console.error('Background applications refresh failed:', error);
        });
        return {
          success: true,
          message: result.message || 'Interview date saved.',
          data: result.data || null,
          emailSent: Boolean(result.data?.email_sent)
        };
      }
      return {
        success: false,
        message: result.message || 'Failed to save interview date.'
      };
    } catch (error) {
      console.error('Error saving interview date:', error);
      return {
        success: false,
        message: error.message || 'Cannot connect to server.'
      };
    }
  };

  const handleSaveRemarks = async (appId) => {
    const normalizedId = String(appId);
    const draftNote = (notesInputs[appId] ?? notesInputs[normalizedId] ?? '').trim();
    if (!draftNote) return;

    const app = applications.find((item) => String(item.id) === normalizedId);
    if (!app) return;

    const currentStatus = (app.status || '').trim();
    const adminT = getT(lang).admin;

    try {
      const response = await fetch(`${API_URL}?action=update_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: app.id,
          newStatus: currentStatus,
          remarks: draftNote,
          ...buildReviewerPayload(currentUser),
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setNotesInputs((prev) => {
          const next = { ...prev };
          delete next[appId];
          delete next[normalizedId];
          return next;
        });
        await fetchApplications();
        alert(adminT.remarksSaved);
        return;
      }
      alert(data.message || adminT.remarksSaveFailed);
    } catch (error) {
      console.error('Error saving remarks:', error);
      alert(adminT.remarksSaveFailed);
    }
  };

  const fetchNotificationsFromAPI = async (email) => {
    if (!email) return null;
    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get_notifications',
          applicant_email: email
        })
      });

      const result = await response.json();

      if (result.status === 'success' && result.data && Array.isArray(result.data)) {
        const apiNotifications = result.data.map((notif) => {
          const meta = Array.isArray(notif.missing_documents)
            ? {}
            : (notif.missing_documents && typeof notif.missing_documents === 'object'
              ? notif.missing_documents
              : {});
          const missingDocs = Array.isArray(notif.missing_documents)
            ? notif.missing_documents
            : (Array.isArray(meta.documents) ? meta.documents : []);

          return {
            id: notif.id,
            name: notif.applicant_name || email,
            email: email,
            message: notif.message,
            createdAt: normalizeMessageCreatedAt(notif.created_at),
            replies: [],
            sender: meta.sender || '',
            is_read: Boolean(notif.is_read),
            notification_type: notif.notification_type,
            missing_documents: missingDocs
          };
        });

        const existingMessages = JSON.parse(localStorage.getItem('helport_messages') || '[]');
        const mergedMessages = mergeApiNotifications(existingMessages, apiNotifications, email);
        persistMessages(mergedMessages);
        setMessages(mergedMessages);
        return mergedMessages;
      }
    } catch (error) {
      console.error('Error fetching notifications from API:', error);
    }
    return null;
  };

  const redirectApplicantAfterLogin = async (user) => {
    let applicantMessages = messages;
    const [application, fetched] = await Promise.all([
      fetchApplicantApplication(user.email),
      fetchNotificationsFromAPI(user.email)
    ]);

    if (fetched) {
      applicantMessages = fetched;
    } else {
      try {
        applicantMessages = JSON.parse(localStorage.getItem('helport_messages') || '[]');
      } catch {
        applicantMessages = messages;
      }
    }

    const access = resolveApplicantAccess(user.email, applicantMessages, application);
    if (access.mode === 'resubmit') {
      setApplyMode('resubmit');
      setApplyInitialStep(access.resubmit.step);
      setCurrentView('apply');
      return;
    }

    if (access.mode === 'waiting') {
      setCurrentView('application-waiting');
      return;
    }

    setCurrentView('home');
  };

  const openApplicantApplyFlow = async () => {
    let application = applicantApplication;
    if (!application && currentUser?.email) {
      application = await fetchApplicantApplication(currentUser.email);
    }

    const access = resolveApplicantAccess(currentUser?.email, messages, application);
    if (access.mode === 'resubmit') {
      setApplyMode('resubmit');
      setApplyInitialStep(access.resubmit.step);
      setCurrentView('apply');
      return;
    }

    if (access.mode === 'waiting') {
      setCurrentView('application-waiting');
      return;
    }

    // Desktop/laptop: require a saved assessment first.
    // Phone / iPad / tablet: go straight to Job Application.
    if (currentUser?.email && !isHandheldDevice()) {
      try {
        const email = encodeURIComponent(String(currentUser.email).trim().toLowerCase());
        const response = await fetch(`${API_URL}?action=get_assessment&email=${email}`);
        const result = await response.json();
        if (result?.status === 'success' && !result?.data) {
          setCurrentView('assessment');
          return;
        }
      } catch {
        // If the check fails, still allow apply so applicants are not hard-blocked offline.
      }
    }

    setApplyMode('new');
    setApplyInitialStep(1);
    setCurrentView('apply');
  };

  const handleApplicationSubmitSuccess = async () => {
    if (currentUser?.email) {
      await fetchApplicantApplication(currentUser.email);
    }
    setCurrentView('application-waiting');
  };

  useEffect(() => {
    localStorage.setItem('helport_messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    let isMounted = true;

    const loadJobDescriptions = async () => {
      const descriptions = await fetchJobDescriptionsMap();
      if (isMounted) {
        setSavedJobDescriptions(descriptions);
      }
    };

    loadJobDescriptions();

    const handleJobDescriptionsUpdated = () => {
      loadJobDescriptions();
    };

    window.addEventListener(JOB_DESCRIPTIONS_UPDATED_EVENT, handleJobDescriptionsUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(JOB_DESCRIPTIONS_UPDATED_EVENT, handleJobDescriptionsUpdated);
    };
  }, []);

  useEffect(() => {
    if (currentView !== 'category') return undefined;

    let isMounted = true;

    const refreshJobDescriptions = async () => {
      const descriptions = await fetchJobDescriptionsMap();
      if (isMounted) {
        setSavedJobDescriptions(descriptions);
      }
    };

    refreshJobDescriptions();

    return () => {
      isMounted = false;
    };
  }, [currentView]);

  // Sync chat across tabs/views via localStorage (API is source of truth across users).
  useEffect(() => {
    const loadStoredMessages = () => {
      const storedMessages = localStorage.getItem('helport_messages');
      if (!storedMessages) return;
      try {
        setMessages(JSON.parse(storedMessages));
      } catch (error) {
        console.error('Error parsing messages from localStorage:', error);
      }
    };

    const handleMessagesUpdate = () => {
      loadStoredMessages();
    };

    const handleStorage = (event) => {
      if (event.key === 'helport_messages') {
        loadStoredMessages();
      }
    };

    window.addEventListener('helport_messages_updated', handleMessagesUpdate);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('helport_messages_updated', handleMessagesUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const isApplicantLoggedIn = Boolean(isLoggedIn && currentUser?.role === 'Applicant');

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) {
      setContactNoticeType('error');
      setContactNotice(ui.common.allFieldsRequired);
      return;
    }

    if (!isApplicantLoggedIn) {
      setContactNoticeType('error');
      setContactNotice(t.contactAccountRequired);
      return;
    }

    const conversationId = getConversationId(currentUser.email);
    const sent = await handleAddReply(conversationId, contactMessage.trim());
    if (sent === false) return;

    setContactMessage('');
    setContactNoticeType('success');
    setContactNotice(ui.common.messageSent);
  };

  useEffect(() => {
    if (!isApplicantLoggedIn || !currentUser) return;
    const displayName = (currentUser.full_name || currentUser.name || '').trim();
    if (displayName) setContactName(displayName);
    if (currentUser.email) setContactEmail(currentUser.email.trim());
  }, [isApplicantLoggedIn, currentUser]);

  // Auto-fetch data kapag naka-login na ang staff/admin
  useEffect(() => {
    if (isLoggedIn && currentUser?.role !== 'Applicant') {
      fetchApplications();
      fetchAssignments();
      fetchContactMessagesFromAPI().then((rows) => {
        if (!rows) return;
        setMessages((prev) => persistMessages(mergeContactThreadMessages(prev, rows)));
      });
      const interval = setInterval(() => {
        fetchApplications();
        fetchAssignments();
      }, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run admin bootstrap when login/role changes
  }, [isLoggedIn, currentUser]);

  // Live chat sync for admin and applicants (no manual refresh needed)
  useEffect(() => {
    if (!isLoggedIn || !currentUser) return undefined;

    const syncMessages = async () => {
      if (currentUser.role === 'Applicant' && currentUser.email) {
        await fetchNotificationsFromAPI(currentUser.email);
        return;
      }

      if (currentUser.role !== 'Applicant') {
        const rows = await fetchContactMessagesFromAPI();
        if (!rows) return;
        setMessages((prev) => persistMessages(mergeContactThreadMessages(prev, rows)));
      }
    };

    syncMessages();
    const interval = setInterval(syncMessages, 4000);

    const handleFocus = () => {
      syncMessages();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncMessages();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll messages on login/role changes only
  }, [isLoggedIn, currentUser]);

  useEffect(() => {
    if (isLoggedIn && currentUser?.role === 'Applicant' && currentUser.email) {
      fetchApplicantApplication(currentUser.email);
      fetchNotificationsFromAPI(currentUser.email);
    } else {
      setApplicantApplication(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load applicant data when login/role changes
  }, [isLoggedIn, currentUser]);

  useEffect(() => {
    if (currentView !== 'application-waiting' || !currentUser?.email) return undefined;

    fetchApplicantApplication(currentUser.email);
    const interval = setInterval(() => {
      fetchApplicantApplication(currentUser.email);
    }, 15000);

    return () => clearInterval(interval);
  }, [currentView, currentUser]);

  // Helper to identify Admin-level dashboard users
  const isAdminRole = (user) => isHeadAdminUser(user) || isDepartmentAdminUser(user);

  // Idinagdag para sa user role/data
  const [authInitialTab, setAuthInitialTab] = useState('login');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [applicantSupportOpen, setApplicantSupportOpen] = useState(false);

  const isAppFullView = ['admin', 'apply', 'auth', 'application-waiting'].includes(currentView);
  const showNavbar = ['home', 'category', 'about', 'contact'].includes(currentView)
    || (currentView === 'assessment' && isApplicantLoggedIn);

  const showHeroFooter = currentView === 'home';

  // Helport Chat: HOME page only, for logged-in applicants (employees)
  const showApplicantSupport = isApplicantLoggedIn && currentView === 'home';

  const openApplicantSupport = () => setApplicantSupportOpen(true);
  const closeApplicantSupport = () => setApplicantSupportOpen(false);
  const toggleApplicantSupport = () => setApplicantSupportOpen((open) => !open);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setAssignments({});
    closeMobileMenu();
    clearAuthSession();
    try {
      localStorage.removeItem('helport_assignments');
    } catch {
      // ignore storage errors
    }
    setCurrentView('home');
  };

  const navigateFromMobileMenu = (view) => {
    setCurrentView(view);
    closeMobileMenu();
  };

  const openAuthFromMobileMenu = (tab) => {
    setAuthInitialTab(tab);
    setCurrentView('auth');
    closeMobileMenu();
  };

  useEffect(() => {
    if (authBootstrapped || !isLoggedIn || !currentUser) {
      return undefined;
    }

    let cancelled = false;
    const viewAtBootstrap = currentView;

    (async () => {
      try {
        if (currentUser.role !== 'Applicant') {
          // Prefetch dashboard data, but stay on the current view (landing
          // page by default). Do not force-redirect staff/admin to dashboard.
          await Promise.all([fetchApplications(), fetchAssignments()]);
        } else if (currentUser.email && viewAtBootstrap !== 'home') {
          await redirectApplicantAfterLogin(currentUser);
        }
      } finally {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // One-shot auth restore only — do not re-run when view/handlers change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authBootstrapped, isLoggedIn, currentUser]);

  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    const employeeViews = ['admin'];
    const applicantViews = ['application-waiting', 'apply', 'assessment', 'home'];

    if (currentUser.role !== 'Applicant' && employeeViews.includes(currentView)) {
      saveAuthView(currentView);
      syncAuthSessionSnapshot(currentUser, currentView);
      return;
    }

    if (currentUser.role !== 'Applicant' && currentView === 'home') {
      saveAuthView('home');
      syncAuthSessionSnapshot(currentUser, 'home');
      return;
    }

    if (currentUser.role === 'Applicant' && applicantViews.includes(currentView)) {
      saveAuthView(currentView);
      syncAuthSessionSnapshot(currentUser, currentView);
    }
  }, [currentView, isLoggedIn, currentUser]);

  useEffect(() => {
    if (!isLoggedIn || !currentUser || typeof window === 'undefined') return undefined;

    const persistSession = () => {
      syncAuthSessionSnapshot(currentUser, loadAuthView() || currentView);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistSession();
      }
    };

    persistSession();
    window.addEventListener('pagehide', persistSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', persistSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn, currentUser, currentView]);

  useEffect(() => {
    closeMobileMenu();
  }, [currentView]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (document.querySelector('.attachment-modal')) {
        return;
      }

      if (applicantSupportOpen) {
        closeApplicantSupport();
        return;
      }

      if (isMobileMenuOpen) {
        closeMobileMenu();
        return;
      }

      if (selectedPosition) {
        setSelectedPosition(null);
        return;
      }

      const backViews = ['auth', 'apply', 'application-waiting', 'admin', 'assessment'];
      if (backViews.includes(currentView)) {
        setCurrentView('home');
      }
    };

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen, selectedPosition, currentView, applicantSupportOpen]);

  useEffect(() => {
    if (currentView === 'assessment' && !isApplicantLoggedIn) {
      setAuthInitialTab('login');
      setCurrentView('auth');
    }
  }, [currentView, isApplicantLoggedIn]);

  useEffect(() => {
    if (currentView === 'applicant') {
      setCurrentView('home');
      setApplicantSupportOpen(true);
    }
  }, [currentView]);

  // Close Helport Chat when leaving HOME
  useEffect(() => {
    if (currentView !== 'home' && applicantSupportOpen) {
      setApplicantSupportOpen(false);
    }
  }, [currentView, applicantSupportOpen]);

  return (
    <div className={`hero-wrapper ${isAppFullView ? 'admin-view' : ''} view-${currentView}`}>
      
      {/* Ipakita ang Navbar kapag nasa public pages */}
      {showNavbar ? (
        <nav className="navbar mobile-nav">
          <div className="nav-logo-container" onClick={() => navigateFromMobileMenu('home')} style={{cursor: 'pointer'}}>
            <img src={hLogoImg} alt="Helport" className="nav-logo-full" />
            <img src={hIconImg} alt="" className="nav-icon nav-icon-desktop" aria-hidden="true" />
          </div>
          <div className="nav-links" style={{ display: 'flex', alignItems: 'center' }}>
            <button 
              className={currentView === 'home' ? 'active' : ''} 
              onClick={() => setCurrentView('home')}
            >{t.home}</button>
            <button 
              className={currentView === 'category' ? 'active' : ''} 
              onClick={() => setCurrentView('category')}
            >{t.category}</button>
            <button 
              className={currentView === 'about' ? 'active' : ''} 
              onClick={() => setCurrentView('about')}
            >{t.aboutUs}</button>
            <button 
              className={currentView === 'contact' ? 'active' : ''} 
              onClick={() => setCurrentView('contact')}
            >{t.contact}</button>
            {isApplicantLoggedIn && (
              <button
                className={currentView === 'assessment' ? 'active' : ''}
                onClick={() => setCurrentView('assessment')}
              >{t.assessment}</button>
            )}

            {isLoggedIn && currentUser && currentUser.role !== 'Applicant' && (
               <button onClick={() => setCurrentView('admin')}>{t.dashboard}</button>
            )}

            <select 
              value={lang} 
              onChange={(e) => handleLangChange(e.target.value)}
              className="lang-selector"
            >
              <option value="en">{ui.common.english}</option>
              <option value="zh">{ui.common.chinese}</option>
            </select>
          </div>
          <div className="auth-btns">
            {isLoggedIn ? (
              <button 
                className="btn-signup" 
                onClick={handleLogout}
                style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
              >
                {ui.common.logout}
              </button>
            ) : (
              <>
                <span className="login-link" onClick={() => openAuthFromMobileMenu('login')} style={{cursor: 'pointer'}}>{t.login}</span>
                <button className="btn-signup" onClick={() => openAuthFromMobileMenu('signup')}>{t.signUp}</button>
              </>
            )}
          </div>

          <button
            type="button"
            className={`mobile-menu-toggle${isMobileMenuOpen ? ' is-open' : ''}`}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>

          <div
            className={`mobile-menu-backdrop${isMobileMenuOpen ? ' open' : ''}`}
            onClick={closeMobileMenu}
            aria-hidden={!isMobileMenuOpen}
          />

          <aside className={`mobile-menu-drawer${isMobileMenuOpen ? ' open' : ''}`} aria-hidden={!isMobileMenuOpen}>
            <div className="mobile-menu-header">
              <img src={hLogoImg} alt="Helport" className="mobile-menu-logo" />
              <button
                type="button"
                className="mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                ×
              </button>
            </div>

            <nav className="mobile-menu-links">
              <button type="button" className={currentView === 'home' ? 'active' : ''} onClick={() => navigateFromMobileMenu('home')}>{t.home}</button>
              <button type="button" className={currentView === 'category' ? 'active' : ''} onClick={() => navigateFromMobileMenu('category')}>{t.category}</button>
              <button type="button" className={currentView === 'about' ? 'active' : ''} onClick={() => navigateFromMobileMenu('about')}>{t.aboutUs}</button>
              <button type="button" className={currentView === 'contact' ? 'active' : ''} onClick={() => navigateFromMobileMenu('contact')}>{t.contact}</button>

              {isApplicantLoggedIn && (
                <button type="button" className={currentView === 'assessment' ? 'active' : ''} onClick={() => navigateFromMobileMenu('assessment')}>{t.assessment}</button>
              )}

              {isLoggedIn && currentUser && currentUser.role !== 'Applicant' && (
                <button type="button" className={currentView === 'admin' ? 'active' : ''} onClick={() => navigateFromMobileMenu('admin')}>{t.dashboard}</button>
              )}
            </nav>

            <div className="mobile-menu-divider" />

            <div className="mobile-menu-lang">
              <span className="mobile-menu-lang-label">{ui.common.language}</span>
              <div className="mobile-menu-lang-options">
                <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => handleLangChange('en')}>{ui.common.english}</button>
                <button type="button" className={lang === 'zh' ? 'active' : ''} onClick={() => handleLangChange('zh')}>{ui.common.chinese}</button>
              </div>
            </div>

            <div className="mobile-menu-footer">
              {isLoggedIn ? (
                <button type="button" className="mobile-menu-logout" onClick={handleLogout}>{ui.common.logout}</button>
              ) : (
                <div className="mobile-menu-auth">
                  <button type="button" className="mobile-menu-login" onClick={() => openAuthFromMobileMenu('login')}>{t.login}</button>
                  <button type="button" className="mobile-menu-signup" onClick={() => openAuthFromMobileMenu('signup')}>{t.signUp}</button>
                </div>
              )}
            </div>
          </aside>
        </nav>
      ) : null}

      {/* Main Content Section */}
      <main className={`hero-main ${isAppFullView ? 'admin-view' : ''} ${currentView === 'apply' ? 'apply-view' : ''}`}>
        
        {currentView === 'admin' && (
          isAdminRole(currentUser) ? (
            <AdminDashboard 
              currentUser={currentUser}
              lang={lang}
              onLangChange={handleLangChange}
              onProfileUpdate={(updates) => {
                setCurrentUser((prev) => {
                  if (!prev) return prev;
                  updateAuthSession(updates);
                  return { ...prev, ...updates };
                });
              }}
              onBack={() => setCurrentView('home')}
              applications={applications}
              assignments={assignments}
              setAssignments={setAssignments}
              onSaveAssignment={saveAssignment}
              fetchApplications={fetchApplications}
              onFetchAssignments={fetchAssignments}
              onSaveInterviewDate={saveInterviewDate}
              handleUpdateStatus={handleUpdateStatus}
              onClaimReview={handleClaimReview}
              onAssignAccount={handleAssignAccount}
              onRetractApplication={handleRetractApplication}
              handleSaveRemarks={handleSaveRemarks}
              notesInputs={notesInputs}
              handleNoteChange={handleNoteChange}
              messages={messages}
              onReplyMessage={handleAddReply}
              onDeleteMessage={handleDeleteMessage}
              onEditChatMessage={handleEditChatMessage}
              onDeleteChatMessage={handleDeleteChatMessage}
              hiddenMessageIds={hiddenMessageIds}
            />
          ) : (
            <StaffDashboard 
              currentUser={currentUser}
              lang={lang}
              onLangChange={handleLangChange}
              applications={applications}
              assignments={assignments}
              fetchApplications={fetchApplications}
              onFetchAssignments={fetchAssignments}
              handleUpdateStatus={handleUpdateStatus}
              onClaimReview={handleClaimReview}
              notesInputs={notesInputs}
              handleNoteChange={handleNoteChange}
              messages={messages}
              onReplyMessage={handleAddReply}
              onDeleteMessage={handleDeleteMessage}
              onEditChatMessage={handleEditChatMessage}
              onDeleteChatMessage={handleDeleteChatMessage}
              hiddenMessageIds={hiddenMessageIds}
            />
          )
        )}

        {currentView === 'application-waiting' && (
          <ApplicationWaiting
            lang={lang}
            application={applicantApplication}
            onBack={() => setCurrentView('home')}
            onGoToMessages={openApplicantSupport}
          />
        )}

        {currentView === 'apply' && (
          <ApplyForm
            lang={lang}
            onBack={() => setCurrentView('home')}
            initialStep={applyInitialStep}
            applyMode={applyMode}
            onSubmitSuccess={handleApplicationSubmitSuccess}
          />
        )}

        {currentView === 'auth' && (
          <AuthPortal
            lang={lang}
            initialTab={authInitialTab}
            onBack={() => setCurrentView('home')}
            onLoginSuccess={async (user) => {
              setIsLoggedIn(true);
              setCurrentUser(user);
              setAssignments({});
              try {
                localStorage.removeItem('helport_assignments');
              } catch {
                // ignore storage errors
              }

              if (user.role !== 'Applicant') {
                saveAuthSession(user, 'admin');
                await Promise.all([fetchApplications(), fetchAssignments()]);
                setCurrentView('admin');
                setAuthBootstrapped(true);
                return;
              }

              saveAuthSession(user);

              if (user.email) {
                await redirectApplicantAfterLogin(user);
                setAuthBootstrapped(true);
                return;
              }

              setCurrentView('home');
              setAuthBootstrapped(true);
            }}
          />
        )}

        {currentView === 'home' && (
          <>
            <section className="hero-left">
              <span className="hero-badge animate-in">
                <span className="hero-badge-dot" aria-hidden="true"></span>
                {t.helportPhilippines}
              </span>
              <img src={hLogoImg} alt="Helport" className="logo-display animate-in delay-1" />
              <h1 className="hero-title animate-in delay-1">
                {t.heroTitle}{' '}
                <span className="hero-title-accent">{t.heroTitleAccent}</span>
              </h1>
              <p className="hero-description animate-in delay-2">{t.heroDesc}</p>

              <div className="action-group animate-in delay-3">
                {/* Iche-check muna kung naka-login. Kung hindi, ididirekta sa Signup portal */}
                <button className="apply-btn" onClick={() => {
                  if (isLoggedIn) {
                    if (currentUser && currentUser.role !== 'Applicant') {
                      setCurrentView('admin');
                    } else {
                      openApplicantApplyFlow();
                    }
                  } else {
                    setAuthInitialTab('signup'); // Pupunta sa Register Account tab
                    setCurrentView('auth');
                  }
                }}>
                  {t.applyNow}
                  <span className="apply-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </span>
                </button>

                <button className="learn-more" onClick={() => setCurrentView('about')}>
                  {t.learnMore}
                  <span className="learn-arrow" aria-hidden="true"></span>
                </button>
              </div>
            </section>

            <section className="hero-right animate-in delay-2">
              <div className="ai-image-container animate-float">
                <div className="ai-image-ring" aria-hidden="true"></div>
                <img src={hpAiImg} alt="AI Synergy" className="hpai-visual" />
                <div className="glow-effect"></div>
              </div>
            </section>
          </>
        )}

        {/* ================= CATEGORY PAGE ================= */}
        {currentView === 'category' && (
          <section className="info-section category-section fade-in-section">
            <div className="category-list-container">
              <div className="category-page-header">
                <h2 className="neon-heading category-title">{t.category}</h2>
                <p className="category-page-sub">{t.categoryIntro}</p>
              </div>
              <div className="category-positions-scroll">
              <div className="category-grid">
                {positions.map((pos, idx) => (
                  <button
                    key={pos}
                    className="category-btn"
                    onClick={() => setSelectedPosition(pos)}
                  >
                    <span className="category-btn-num" aria-hidden="true">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="category-btn-label">{getPositionLabel(lang, pos)}</span>
                    <span className="category-btn-arrow" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 6l6 6-6 6"/>
                      </svg>
                    </span>
                  </button>
                ))}
              </div>
              </div>
            </div>
          </section>
        )}

        {currentView === 'category' && selectedPosition && createPortal(
          <div className="category-modal-overlay modal-backdrop-in" onClick={() => setSelectedPosition(null)}>
            <div className="category-modal-content modal-content-in" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setSelectedPosition(null)}>×</button>
              <h3>{getPositionLabel(lang, selectedPosition)}</h3>
              <p>{resolvePositionDescription(lang, selectedPosition, savedJobDescriptions) || ui.common.noDescription}</p>
            </div>
          </div>,
          document.body
        )}

        {/* ================= ABOUT US PAGE ================= */}
        {currentView === 'about' && (
          <section className="info-section fade-in-section">
            <div className="page-panel">
              <h2 className="neon-heading">{t.aboutTitle}</h2>
              <div className="info-content">
                <p>
                  <strong>{t.aboutStrong}</strong>{t.aboutText1}
                </p>
                <p>
                  {t.aboutText2}
                </p>
                <div className="mission-vision">
                  <div className="mv-box">
                    <h3>{t.missionTitle}</h3>
                    <p>{t.missionDesc}</p>
                  </div>
                  <div className="mv-box">
                    <h3>{t.visionTitle}</h3>
                    <p>{t.visionDesc}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ================= CONTACT PAGE ================= */}
        {currentView === 'assessment' && isApplicantLoggedIn && (
          <AssessmentPage
            lang={lang}
            userEmail={currentUser?.email || ''}
            userName={currentUser?.full_name || currentUser?.name || ''}
            onApply={() => {
              if (isLoggedIn) {
                if (currentUser && currentUser.role !== 'Applicant') {
                  setCurrentView('admin');
                } else {
                  openApplicantApplyFlow();
                }
              } else {
                setAuthInitialTab('signup');
                setCurrentView('auth');
              }
            }}
          />
        )}

        {currentView === 'contact' && (
          <section className="info-section fade-in-section">
            <div className="page-panel contact-panel">
              <h2 className="neon-heading">{t.getInTouch}</h2>
              <div className="contact-container">
                
                <div className="contact-details">
                  <h3>{t.contactInfo}</h3>
                  <p>{t.contactSub}</p>
                  <ul>
                    <li>
                      <span className="icon" aria-hidden="true"><ContactLocationIcon /></span>
                      <span className="contact-item-text">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            '23rd Floor, IBM Plaza, Eastwood City Cyberpark, 188 E. Rodriguez Jr. Ave., Bagumbayan, Quezon City, Philippines'
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contact-maps-link"
                        >
                          {t.address}
                        </a>
                      </span>
                    </li>
                    <li>
                      <span className="icon" aria-hidden="true"><ContactEmailIcon /></span>
                      <span className="contact-item-text">
                        <a href="mailto:hpai_recruitment@helport.ai">hpai_recruitment@helport.ai</a>
                      </span>
                    </li>
                    <li>
                      <span className="icon" aria-hidden="true"><ContactPhoneIcon /></span>
                      <span className="contact-item-text">
                        <a href="tel:+639304475805">+63 930 447 5805</a>
                      </span>
                    </li>
                    <li>
                      <span className="icon" aria-hidden="true"><ContactWebsiteIcon /></span>
                      <span className="contact-item-text">
                        <a href="https://www.helport.ai" target="_blank" rel="noopener noreferrer">www.helport.ai</a>
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="contact-form-box">
                  <form onSubmit={handleSendMessage}>
                    {contactNotice && (
                      <div className={`contact-notice contact-notice--${contactNoticeType}`}>
                        {contactNotice}
                      </div>
                    )}
                    <div className="c-form-group">
                      <label>{t.name}</label>
                      <input
                        type="text"
                        placeholder={t.namePlaceholder}
                        value={contactName}
                        onChange={(e) => {
                          setContactNotice('');
                          setContactName(e.target.value);
                        }}
                        required
                      />
                    </div>
                    <div className="c-form-group">
                      <label>{t.email}</label>
                      <input
                        type="email"
                        placeholder={t.emailPlaceholder}
                        value={contactEmail}
                        onChange={(e) => {
                          setContactNotice('');
                          setContactEmail(e.target.value);
                        }}
                        required
                        readOnly={isApplicantLoggedIn}
                      />
                    </div>
                    <div className="c-form-group">
                      <label>{t.message}</label>
                      <textarea
                        placeholder={t.messagePlaceholder}
                        rows="4"
                        value={contactMessage}
                        onChange={(e) => {
                          setContactNotice('');
                          setContactMessage(e.target.value);
                        }}
                        required
                      ></textarea>
                    </div>
                    <button type="submit" className="apply-btn send-btn">{t.sendMessage}</button>
                  </form>
                </div>

              </div>
            </div>
          </section>
        )}

      </main>

      {showHeroFooter ? (
        <footer className="hero-footer">
          {!applicantSupportOpen && (
            <p className="hero-footer-credits">{ui.applicant.developerCredit}</p>
          )}
          <p className="hero-footer-supported">{t.supportedBy}</p>
        </footer>
      ) : null}

      {showApplicantSupport && (
        <ApplicantSupportWidget
          isOpen={applicantSupportOpen}
          onToggle={toggleApplicantSupport}
          onClose={closeApplicantSupport}
          lang={lang}
          currentUser={currentUser}
          messages={messages}
          application={applicantApplication}
          onReplyMessage={handleAddReply}
          onEditChatMessage={handleEditChatMessage}
          onDeleteChatMessage={handleDeleteChatMessage}
          hiddenMessageIds={hiddenMessageIds}
          onGoToApply={(targetStep) => {
            closeApplicantSupport();
            setApplyMode('resubmit');
            setApplyInitialStep(targetStep || 1);
            setCurrentView('apply');
          }}
        />
      )}
    </div>
  );
}

export default App;